'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import LearningPathEnrollButton from './learning-path-enroll-button';
import { LearningPathStats } from './learning-path-stats';
import { isLearningPathCompleted, getProgressDisplayPercent } from '@/lib/utils/learning-path-utils';

interface LearningPathStatsData {
  courses: number;
  videos: number;
  quizzes: number;
  assessments: number;
}

interface LearningPathImage {
  url: string;
  alt?: string;
}

export interface LearningPathCardData {
  image: LearningPathImage | string;
  alt?: string;
  smallLabel?: string;
  title: string;
  description?: string;
  certificateLabel?: string;
  stats?: LearningPathStatsData;
  isEnrolled?: boolean;
  /** When true, user has started the path. Used with LearningPathEnrollButton. */
  hasStarted?: boolean;
  progressPercentage?: number;
  /** When true, show Completed. When false, never show Completed. When undefined, use progressPercentage >= 100 */
  isCompleted?: boolean;
  /** When true, all courses/lessons done; assessment may be pending. Used for progress bar and Take Assessment. */
  isLearningMaterialCompleted?: boolean;
  /** When true, LP has assessment. Used for Take Assessment button. */
  hasAssessment?: boolean;
  enrollLink?: string;
  resumeLink?: string;
  href?: string;
  /** When provided, use LearningPathEnrollButton for enrollment flow (landing page). When omitted, use plain navigation (profile). */
  learningPathUid?: string;
}

interface LearningPathCardProps {
  path: LearningPathCardData;
  variant?: 'home' | 'profile';
  className?: string;
  /** Set true for the first card on home to improve LCP (eager load, fetchpriority=high) */
  priority?: boolean;
}

export function LearningPathCard({ path, variant = 'home', className = '', priority = false }: LearningPathCardProps) {
  const router = useRouter();
  const isCompleted = isLearningPathCompleted(path.isCompleted, path.progressPercentage);
  const isTakeAssessment =
    path.hasAssessment && path.isLearningMaterialCompleted && !isCompleted;
  const displayPercent = getProgressDisplayPercent(
    path.progressPercentage,
    path.isCompleted,
    path.isLearningMaterialCompleted
  );

  // Handle both image formats: string or object; avoid empty src (Next.js Image requirement)
  const rawImageUrl = typeof path.image === 'string' ? path.image : path.image?.url;
  const imageUrl = rawImageUrl && rawImageUrl.trim() !== '' ? rawImageUrl : null;
  const imageAlt = typeof path.image === 'string'
    ? (path.alt || path.title)
    : (path.image?.alt || path.alt || path.title);

  const cardLink = path.isEnrolled
    ? (path.resumeLink || path.href || '#')
    : (path.enrollLink || path.href || '#');
  const buttonText = path.isEnrolled
    ? isCompleted
      ? 'Completed'
      : isTakeAssessment
        ? 'Take Assessment'
        : 'Resume'
    : 'Enroll now';

  const handleCardClick = () => {
    if (cardLink && cardLink !== '#') {
      const href = cardLink.startsWith('/') ? cardLink : `/${cardLink}`;
      router.push(href);
    }
  };

  // Variant-specific classes
  const isProfile = variant === 'profile';
  const cardWrapperClass = isProfile
    ? `flex flex-col xl:flex-row gap-0 transition-all cursor-pointer rounded-lg card-hover-zoom group ${className}`
    : `flex flex-col w-full h-full transition-all cursor-pointer rounded-lg card-hover-zoom group ${className}`;

  const contentBgClass = 'achievement-card-bg';

  // Image dimensions based on variant
  const mobileImageHeight = isProfile ? 'h-[200px] tab:h-[300px] desktop:h-[355px]' : 'h-[175px] tab:h-[420px] desktop:h-[355px]';
  const desktopImageClass = isProfile ? 'hidden xl:block xl:w-72 xl:h-full' : 'hidden';

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      className={cardWrapperClass}
    >
      {/* Mobile/Tablet: Image on top */}
      <div className={`relative w-full ${mobileImageHeight} ${isProfile ? 'xl:hidden' : ''} overflow-hidden`}>
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={imageAlt ?? path.title}
            fill
            className="object-cover card-zoom-image"
            sizes={isProfile ? '(max-width: 810px) 100vw, (max-width: 1200px) 50vw, 50vw' : '(max-width: 810px) 100vw, (max-width: 1200px) 50vw, 50vw'}
            priority={priority}
            fetchPriority={priority ? 'high' : undefined}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-3 text-icon-subtle">
            <MaterialIcons iconName="school" className="w-12 h-12" />
          </div>
        )}
      </div>

      {/* Desktop: Image on left (profile only) */}
      {isProfile && (
        <div className={`relative ${desktopImageClass} flex-shrink-0 overflow-hidden`}>
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={imageAlt ?? path.title}
              fill
              className="object-cover card-zoom-image"
              sizes="288px"
              priority={priority}
              fetchPriority={priority ? 'high' : undefined}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-surface-3 text-icon-subtle">
              <MaterialIcons iconName="school" className="w-12 h-12" />
            </div>
          )}
        </div>
      )}

      {/* Content area */}
      <div className={`p-4 lg:p-8 flex gap-6 flex-col ${isProfile ? 'xl:flex-row xl:items-center xl:gap-12' : ''} ${contentBgClass} flex-1 min-h-0`}>
        {/* Title and description section */}
        <div className={`flex-1 flex flex-col min-w-0 ${isProfile ? 'w-full xl:w-auto gap-6 desktop:gap-0' : 'gap-6'}`}>
          {/* Group 1: Label, Title, Description */}
          <div className="flex flex-col gap-2">
            {/* Label and Title group */}
            <div>
              {path.smallLabel && (
                <span className="cs-caption">
                  {path.smallLabel}
                </span>
              )}
              
              {path.title && (
                <h3 className={`type-h3 ${isProfile ? 'line-clamp-1' : ''}`}>{path.title}</h3>
              )}
            </div>

            {path.description && (
              <p className={`cs-m-body ${isProfile ? 'xl:mb-0 line-clamp-4 xl:line-clamp-2' : 'line-clamp-4'}`}>
                {path.description}
              </p>
            )}
          </div>

          {/* Group 2 & 3: Certificate label + Stats (gap-2 mobile/tab, gap-6 desktop) */}
          {(path.certificateLabel || path.stats) && (
            <div className="flex flex-col gap-2 desktop:gap-6">
              {path.certificateLabel && (
                <div className={`flex gap-2 items-center ${isProfile ? 'xl:hidden' : ''}`}>
                  <MaterialIcons
                    iconName="card_membership"
                    className="!text-[20px] !leading-none text-icon-light"
                  />
                  <span className="cs-caption">
                    {path.certificateLabel}
                  </span>
                </div>
              )}
              {path.stats && (
                <LearningPathStats stats={path.stats} variant={variant} />
              )}
            </div>
          )}
        </div>

        {/* Group 4: Button and Progress Bar Row - Default secondary, on card hover primary, on button hover primary brighten */}
        <div>
          {/* Mobile/Tablet */}
          <div className={`flex flex-col tab:flex-row items-stretch tab:items-center gap-4 mt-auto ${isProfile ? 'xl:hidden' : ''}`}>
            {path.learningPathUid ? (
              <LearningPathEnrollButton
                learningPathUid={path.learningPathUid}
                initialIsSubscribed={path.isEnrolled}
                initialHasStarted={path.hasStarted ?? path.isEnrolled}
                progressPercent={path.progressPercentage}
                isCompleted={path.isCompleted}
                isLearningMaterialCompleted={path.isLearningMaterialCompleted}
                hasAssessment={path.hasAssessment}
                navigateUrl={cardLink && cardLink !== '#' ? (cardLink.startsWith('/') ? cardLink : `/${cardLink}`) : undefined}
                variant="SecondaryFillButton"
                className={`w-full tab:w-auto whitespace-nowrap tab:shrink-0 order-2 tab:order-1 tab:min-w-[120px] tab:justify-center ${
                  path.isEnrolled && isCompleted
                    ? 'btn-completed-fill-group-hover btn-icon-move'
                    : 'btn-primary-fill-group-hover btn-icon-move'
                }`}
              />
            ) : (
              <Button
                variant="SecondaryFillButton"
                className={`w-full tab:w-auto whitespace-nowrap tab:shrink-0 order-2 tab:order-1 tab:min-w-[120px] tab:justify-center ${
                  path.isEnrolled && isCompleted
                    ? 'btn-completed-fill-group-hover btn-icon-move'
                    : 'btn-primary-fill-group-hover btn-icon-move'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick();
                }}
              >
                <span className="cs-button">{buttonText}</span>
                <MaterialIcons
                  iconName={
                    path.isEnrolled && isCompleted
                      ? 'check_circle'
                      : 'arrow_right_alt'
                  }
                  className={
                    path.isEnrolled && isCompleted
                      ? 'text-inherit btn-icon btn-icon-history-resume !text-[20px] flex-shrink-0 leading-none'
                      : 'text-inherit btn-icon !text-[20px] flex-shrink-0 leading-none transition-transform duration-300 ease-in-out group-hover:translate-x-1'
                  }
                />
              </Button>
            )}

            {path.isEnrolled && (path.progressPercentage !== undefined || path.isCompleted) && (
              <div className="flex flex-col tab:flex-row tab:items-center gap-1 tab:gap-3 w-full order-1 tab:order-2">
                <div className="tab:mb-0 tab:shrink-0 order-1 tab:order-2">
                  <span className="cs-caption text-text-default-subtle">
                    <span
                      className={
                        isCompleted
                          ? 'text-[var(--Color-Extended-Green-Green-Shade)]'
                          : ''
                      }
                    >
                      {displayPercent}%
                    </span>{' '}
                    <span className="cs-caption text-text-default-subtle">Complete</span>
                  </span>
                </div>
                <div className="h-1 w-full bg-surface-5 overflow-hidden order-2 tab:order-1">
                    <div
                      className={`h-full transition-all duration-300 ${
                        isCompleted
                          ? 'bg-[var(--Color-Extended-Green-Green-Shade)]'
                          : 'learning-path-progress-bar'
                      }`}
                      style={{
                        width: `${displayPercent}%`,
                      }}
                    />
                </div>
              </div>
            )}
          </div>

          {/* Desktop: Progress bar and button - vertical layout (profile only) */}
          {isProfile && (
            <div className="hidden xl:flex xl:flex-col gap-4 items-end justify-center shrink-0">
              {path.isEnrolled && (path.progressPercentage !== undefined || path.isCompleted) && (
                <div className="flex flex-col gap-1 flex-initial min-w-[200px]">
                  <span className="cs-caption">
                    <span
                      className={
                        isCompleted
                          ? 'cs-percentage-count text-[var(--Color-Extended-Green-Green-Shade)]'
                          : 'cs-percentage-count'
                      }
                    >
                      {Math.round(displayPercent)}%
                    </span>{' '}
                    <span>Complete</span>
                  </span>
                  <div className="h-2 bg-surface-5 overflow-hidden w-full">
                    <div
                      className={`h-full transition-all duration-300 ${
                        isCompleted
                          ? 'bg-[var(--Color-Extended-Green-Green-Shade)]'
                          : 'learning-path-progress-bar'
                      }`}
                      style={{
                        width: `${displayPercent}%`,
                      }}
                    />
                  </div>
                </div>
              )}
              {path.learningPathUid ? (
                <LearningPathEnrollButton
                  learningPathUid={path.learningPathUid}
                  initialIsSubscribed={path.isEnrolled}
                  initialHasStarted={path.hasStarted ?? path.isEnrolled}
                  progressPercent={path.progressPercentage}
                  isCompleted={path.isCompleted}
                  isLearningMaterialCompleted={path.isLearningMaterialCompleted}
                  hasAssessment={path.hasAssessment}
                  navigateUrl={cardLink && cardLink !== '#' ? (cardLink.startsWith('/') ? cardLink : `/${cardLink}`) : undefined}
                  variant="SecondaryFillButton"
                  className={`whitespace-nowrap shrink-0 w-full min-w-[120px] justify-center ${
                    path.isEnrolled && isCompleted
                      ? 'btn-completed-fill-group-hover btn-icon-move'
                      : 'btn-primary-fill-group-hover btn-icon-move'
                  }`}
                />
              ) : (
                <Button
                  variant="SecondaryFillButton"
                  className={`whitespace-nowrap shrink-0 w-full min-w-[120px] justify-center ${
                    path.isEnrolled && isCompleted
                      ? 'btn-completed-fill-group-hover btn-icon-move'
                      : 'btn-primary-fill-group-hover btn-icon-move'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick();
                  }}
                >
                  <span className="cs-type-s-button-strong">{buttonText}</span>
                  <MaterialIcons
                    iconName={
                      path.isEnrolled && isCompleted
                        ? 'check_circle'
                        : 'arrow_right_alt'
                    }
                    className={
                      path.isEnrolled && isCompleted
                        ? 'text-inherit btn-icon btn-icon-history-resume !text-[20px] flex-shrink-0 leading-none'
                        : 'text-inherit btn-icon !text-[20px] flex-shrink-0 leading-none transition-transform duration-300 ease-in-out group-hover:translate-x-1'
                    }
                  />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
